export {};

declare const __MP_SDK_KEY__: string;

declare global {
  interface Window {
    MP_SDK: any;
    __SHOP_SLUG__?: string;
    dataLayer?: unknown[];
    gtag?: (...args: any[]) => void;
  }
}

type Ga4ParamValue = string | number | boolean | null | undefined;
type Ga4Params = Record<string, Ga4ParamValue>;

function ga4Event(eventName: string, params: Ga4Params) {
  if (typeof window.gtag !== "function") return;
  window.gtag("event", eventName, params);
}

function safeDestinationHost(url: string): string {
  try {
    return new URL(String(url || ""), window.location.href).host;
  } catch (_) {
    return "";
  }
}

function safeDestinationHostWithPath(url: string): string {
  try {
    const u = new URL(String(url || ""), window.location.href);
    // Keep only host + pathname to avoid leaking query params.
    // Normalize to a trailing slash so shop paths are consistent.
    const pathname = u.pathname.endsWith("/") ? u.pathname : `${u.pathname}/`;
    return `${u.host}${pathname}`;
  } catch (_) {
    return "";
  }
}

type ShopConfig = {
  matterportSdk?: {
    key?: string;
    sdkVersion?: string;
  };
  // New schema (preferred)
  ui?: {
    assetsBasePath?: string;
    alwaysVisibleTagLabels?: string[];
    alwaysVisibleTagDistanceMeters?: number;
    defaultTagIcon?: string;
    tagIconOverrides?: Array<{
      label: string;
      icon: string;
    }>;
    showcaseQueryParams?: Record<string, string>;
  };
  shops: Array<{
    id: string;
    name?: { full?: string };
    embed?: { provider?: string; url?: string };
    matterport?: { modelId: string };
    ctaLinks?: Array<{ label: string; href: string }>;
    menuItems?: Array<{ label: string; href: string }>;
    imageUrl?: string;
    serviceBookingUrl?: string;
    testDriveUrl?: string;
    contactUrl?: string;
    siteUrl?: string;
  }>;
};

type NormalizedTagIconOverride = {
  matchLabel: string;
  iconUrl: string;
  iconId: string;
};

function normalizeTagLabel(input: unknown): string {
  return String(input ?? "")
    .replace(/\u3000/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function isAbsoluteIconRef(icon: string): boolean {
  const trimmed = String(icon || "").trim();
  return (
    trimmed.startsWith("/") ||
    trimmed.startsWith("http://") ||
    trimmed.startsWith("https://")
  );
}

function resolveIconUrl(icon: string, assetsBase: string): string {
  const trimmed = String(icon || "").trim();
  if (!trimmed) return "";
  if (isAbsoluteIconRef(trimmed)) return trimmed;
  if (trimmed.includes("/")) return `${assetsBase}/${trimmed}`;
  return `${assetsBase}/icons/${trimmed}`;
}

function stableHash(input: string): string {
  // djb2
  let hash = 5381;
  for (let i = 0; i < input.length; i++) {
    hash = ((hash << 5) + hash) ^ input.charCodeAt(i);
  }
  return (hash >>> 0).toString(16);
}

function extractFirstHttpUrlFromText(input: unknown): string {
  const raw = String(input ?? "").trim();
  if (!raw) return "";

  const candidates: string[] = [];

  // Markdown link: [text](https://example.com)
  const md = raw.match(/\[[^\]]*\]\((https?:\/\/[^)\s]+)\)/);
  if (md?.[1]) candidates.push(md[1]);

  // First loose URL in the string
  const loose = raw.match(/https?:\/\/\S+/);
  if (loose?.[0]) candidates.push(loose[0]);

  // Entire string might itself be a URL
  candidates.push(raw);

  for (const candidate of candidates) {
    try {
      const u = new URL(candidate, window.location.origin);
      if (u.protocol === "http:" || u.protocol === "https:")
        return u.toString();
    } catch (_) {
      // ignore
    }
  }
  return "";
}

function shouldShowUrlInIframe(url: string): boolean {
  const u = String(url || "").trim();
  if (!u) return false;
  // Keep iframe usage limited to known embed-style pages.
  return u.includes("audi-interior") || u.includes("audi/information");
}

function normalizeTagIconOverrides(
  config: ShopConfig,
  assetsBase: string,
): NormalizedTagIconOverride[] {
  const fromNew = Array.isArray(config.ui?.tagIconOverrides)
    ? config.ui!.tagIconOverrides!
    : [];

  return fromNew
    .map((o) => {
      const label = normalizeTagLabel(o?.label);
      const icon = String(o?.icon || "").trim();
      if (!label || !icon) return null;
      const iconUrl = resolveIconUrl(icon, assetsBase);
      const iconId = `audi-ui-icon-${stableHash(`${label}:${icon}`)}`;
      return { matchLabel: label, iconUrl, iconId };
    })
    .filter(Boolean) as NormalizedTagIconOverride[];
}

function getShopSlug(): string {
  if (window.__SHOP_SLUG__) return window.__SHOP_SLUG__;
  const parts = window.location.pathname.split("/").filter(Boolean);
  return parts[0] || "";
}

function deriveSlugFromShop(shop: ShopConfig["shops"][number]): string {
  const embedUrl = shop?.embed?.url;
  if (typeof embedUrl === "string") {
    try {
      const u = new URL(embedUrl);
      const parts = u.pathname.split("/").filter(Boolean);
      return parts[parts.length - 1] || shop.id;
    } catch (_) {
      return shop.id;
    }
  }
  return shop.id;
}

function applySweepParamsFromPageUrl(params: URLSearchParams) {
  // If the page URL has sweep-related params, pass them through to Showcase.
  // Example: ?ss=9&sr=-2.64,-.79
  const pageParams = new URLSearchParams(window.location.search);
  const ss = pageParams.get("ss");
  const sr = pageParams.get("sr");

  if (ss) params.set("ss", ss);
  if (sr) params.set("sr", sr);

  // Disable auto-tour when starting from a specific sweep position
  if (ss || sr) params.set("ts", "-1");
}

async function fetchJson<T>(url: string): Promise<T> {
  const res = await fetch(url, { cache: "no-store" });
  if (!res.ok) throw new Error(`Failed to fetch ${url}: ${res.status}`);
  return (await res.json()) as T;
}

function show(elm: HTMLElement) {
  elm.style.display = "";
}

function hide(elm: HTMLElement) {
  elm.style.display = "none";
}

function setClass(elm: HTMLElement, className: string, on: boolean) {
  if (on) elm.classList.add(className);
  else elm.classList.remove(className);
}

function injectCssIntoIframeHead(iframe: HTMLIFrameElement, href: string) {
  try {
    const doc = iframe.contentDocument;
    if (!doc) return;
    const head = doc.head;
    if (!head) return;

    const markerAttr = "data-audi-showcase-css";
    const existing = head.querySelector(
      `link[rel="stylesheet"][${markerAttr}="1"]`,
    ) as HTMLLinkElement | null;

    if (existing) {
      // Keep href current in case assetsBasePath changed.
      if (existing.getAttribute("href") !== href)
        existing.setAttribute("href", href);
      return;
    }

    const link = doc.createElement("link");
    link.rel = "stylesheet";
    link.href = href;
    link.setAttribute(markerAttr, "1");
    head.appendChild(link);
  } catch (_e) {
    // Likely cross-origin; ignore.
  }
}

let showcaseHighlightsObserver: MutationObserver | null = null;
let shareDialogObserver: MutationObserver | null = null;

function cleanShareUrl(url: string): string {
  try {
    const u = new URL(url);
    const ss = u.searchParams.get("ss");
    const sr = u.searchParams.get("sr");

    // Clear all params and only keep ss/sr if present
    u.search = "";
    if (ss) u.searchParams.set("ss", ss);
    if (sr) u.searchParams.set("sr", sr);

    return u.toString();
  } catch (_) {
    return url;
  }
}

function rewriteUrlInText(text: string, addNewlineAfterUrl = false): string {
  // Find and replace URLs in text that contain our host
  // Match only ASCII printable characters (0x21-0x7E) to exclude Japanese text
  const escapedHost = window.location.host.replace(
    /[.*+?^${}()|[\]\\]/g,
    "\\$&",
  );
  const urlRegex = new RegExp(`https?://${escapedHost}[!-~]*`, "g");
  return text.replace(urlRegex, (match) => {
    const cleaned = cleanShareUrl(match);
    return addNewlineAfterUrl ? cleaned + "\n" : cleaned;
  });
}

function rewriteSnsShareUrl(url: string): string {
  try {
    const u = new URL(url);

    // Facebook: sharer.php?u=
    if (/facebook\.com.*sharer/.test(url)) {
      const sharedUrl = u.searchParams.get("u");
      if (sharedUrl && sharedUrl.includes(window.location.host)) {
        u.searchParams.set("u", cleanShareUrl(sharedUrl));
        return u.toString();
      }
    }

    // X/Twitter: intent/tweet?text= (URL is embedded in text)
    if (/(?:twitter\.com|x\.com).*intent/.test(url)) {
      const text = u.searchParams.get("text");
      if (text && text.includes(window.location.host)) {
        u.searchParams.set("text", rewriteUrlInText(text));
        return u.toString();
      }
      const urlParam = u.searchParams.get("url");
      if (urlParam && urlParam.includes(window.location.host)) {
        u.searchParams.set("url", cleanShareUrl(urlParam));
        return u.toString();
      }
    }

    // LinkedIn: shareArticle?url=
    if (/linkedin\.com.*shareArticle/.test(url)) {
      const sharedUrl = u.searchParams.get("url");
      if (sharedUrl && sharedUrl.includes(window.location.host)) {
        u.searchParams.set("url", cleanShareUrl(sharedUrl));
        return u.toString();
      }
    }

    // Pinterest: pin/create/link?url= (also jp.pinterest.com)
    if (/pinterest\.com.*pin\/create/.test(url)) {
      const sharedUrl = u.searchParams.get("url");
      if (sharedUrl && sharedUrl.includes(window.location.host)) {
        u.searchParams.set("url", cleanShareUrl(sharedUrl));
        u.searchParams.delete("media"); // Remove invalid media URL
        return u.toString();
      }
    }

    // Handle mailto: links
    if (url.startsWith("mailto:")) {
      const mailtoMatch = url.match(/^mailto:([^?]*)\??(.*)/);
      if (mailtoMatch) {
        const recipient = mailtoMatch[1] || "";
        const queryString = mailtoMatch[2] || "";
        const params = new URLSearchParams(queryString);
        let changed = false;

        for (const [key, val] of params.entries()) {
          if (val.includes(window.location.host)) {
            // Add newline after URL in email body to separate from following text
            const rewritten = rewriteUrlInText(val, true);
            if (rewritten !== val) {
              params.set(key, rewritten);
              changed = true;
            }
          }
        }

        if (changed) {
          return `mailto:${recipient}?${params.toString()}`;
        }
      }
    }
  } catch (_) {
    // ignore
  }
  return url;
}

function setupShareLinkOverrides(iframe: HTMLIFrameElement) {
  try {
    const doc = iframe.contentDocument;
    const win = iframe.contentWindow;
    if (!doc || !doc.body || !win) return;

    if (shareDialogObserver) {
      shareDialogObserver.disconnect();
      shareDialogObserver = null;
    }

    // Override window.open to rewrite SNS share URLs
    const originalWindowOpen = win.open.bind(win);
    (win as any).open = (
      url?: string | URL,
      target?: string,
      features?: string,
    ) => {
      if (url) {
        const urlStr = String(url);
        const rewritten = rewriteSnsShareUrl(urlStr);
        return originalWindowOpen(rewritten, target, features);
      }
      return originalWindowOpen(url, target, features);
    };

    // Hide tooltips when share modal is open
    let isShareModalOpen = false;
    let manualHideTimestamp = 0;
    const setTooltipsHidden = (hidden: boolean, isManual = false) => {
      if (isManual && hidden) {
        manualHideTimestamp = Date.now();
      }
      const tooltips = document.querySelectorAll(".pano-tag-tooltip");
      tooltips.forEach((tooltip) => {
        const el = tooltip as HTMLElement;
        if (hidden) {
          el.dataset.hiddenByModal = "1";
          el.style.visibility = "hidden";
        } else {
          delete el.dataset.hiddenByModal;
          el.style.visibility = "";
        }
      });
    };

    const detectShareModalOpen = (): boolean => {
      // Method 1: Check aria-expanded on modal buttons (share, VR)
      const modalButtons = doc.querySelectorAll(
        'button[aria-label="このSpaceを共有"], button[aria-label="VRで表示"]',
      );
      for (const btn of modalButtons) {
        const expanded = btn.getAttribute("aria-expanded");
        if (expanded === "true") {
          return true;
        }
      }

      // Method 2: Check for modal/dialog elements
      const dialog = doc.querySelector('[role="dialog"]');
      if (dialog) {
        return true;
      }

      // Method 3: Check for share dialog container (Matterport specific)
      // The share dialog typically has class containing "share" and is visible
      const shareDialog = doc.querySelector(
        '[class*="share"][class*="container"], [class*="share"][class*="modal"], [class*="share"][class*="panel"]',
      );
      if (shareDialog && (shareDialog as HTMLElement).offsetParent !== null) {
        return true;
      }

      return false;
    };

    const updateTooltipVisibility = () => {
      // Skip if manually hidden recently (within 1 second) to prevent race conditions
      if (manualHideTimestamp && Date.now() - manualHideTimestamp < 1000) {
        return;
      }

      const wasOpen = isShareModalOpen;
      isShareModalOpen = detectShareModalOpen();

      if (wasOpen !== isShareModalOpen) {
        setTooltipsHidden(isShareModalOpen);
      }
    };

    // Add click listener to buttons that open modals (share, VR)
    const setupModalButtonListeners = () => {
      const modalButtons = doc.querySelectorAll(
        'button[aria-label="このSpaceを共有"], button[aria-label="VRで表示"]',
      );
      modalButtons.forEach((btn) => {
        const button = btn as HTMLElement;
        if (!button.dataset.tooltipListenerAdded) {
          button.dataset.tooltipListenerAdded = "1";
          button.addEventListener("click", () => {
            // Immediately hide tooltips when modal button is clicked
            setTooltipsHidden(true, true); // isManual = true
            isShareModalOpen = true;
          });
        }
      });
    };

    // Listen for any click in the iframe document to detect modal close
    doc.addEventListener(
      "click",
      (e) => {
        const target = e.target as HTMLElement;
        // If clicking modal buttons, tooltips are already hidden above
        if (
          target.closest('button[aria-label="このSpaceを共有"]') ||
          target.closest('button[aria-label="VRで表示"]')
        ) {
          return;
        }

        // Delay to let modal state settle, then check
        setTimeout(() => {
          updateTooltipVisibility();
        }, 150);
      },
      true,
    );

    // Also check periodically in case modal is closed by other means (ESC key, etc.)
    setInterval(() => {
      if (isShareModalOpen) {
        const stillOpen = detectShareModalOpen();
        if (!stillOpen) {
          isShareModalOpen = false;
          setTooltipsHidden(false);
        }
      }
    }, 500);

    setupModalButtonListeners();

    const rewriteShareUrls = () => {
      updateTooltipVisibility();
      // Find share dialog input fields
      const inputs = doc.querySelectorAll(
        'input[type="text"], input[readonly]',
      );
      inputs.forEach((input) => {
        const el = input as HTMLInputElement;
        const val = el.value || "";
        // Check if it looks like a share URL with our host
        if (val.includes(window.location.host) && val.includes("?")) {
          const cleaned = cleanShareUrl(val);
          if (cleaned !== val) {
            el.value = cleaned;
          }
        }
      });

      // Also check textarea elements
      const textareas = doc.querySelectorAll("textarea");
      textareas.forEach((ta) => {
        const val = ta.value || "";
        if (val.includes(window.location.host) && val.includes("?")) {
          const cleaned = cleanShareUrl(val);
          if (cleaned !== val) {
            ta.value = cleaned;
          }
        }
      });

      // Rewrite SNS share links (Facebook, X, LinkedIn, Pinterest, Email)
      const shareLinks = doc.querySelectorAll("a[href]");
      shareLinks.forEach((link) => {
        const a = link as HTMLAnchorElement;
        const href = a.href || "";

        // Facebook: sharer.php?u=
        // X/Twitter: intent/tweet?url= or ?text=...&url=
        // LinkedIn: share-offsite/?url=
        // Pinterest: pin/create/button/?url=
        // Email: mailto:?body= or ?subject=...&body=
        const urlParamPatterns = [
          { match: /facebook\.com.*sharer/, param: "u" },
          { match: /twitter\.com.*intent/, param: "url" },
          { match: /x\.com.*intent/, param: "url" },
          { match: /linkedin\.com.*share/, param: "url" },
          { match: /pinterest\.com.*pin/, param: "url" },
        ];

        for (const pattern of urlParamPatterns) {
          if (pattern.match.test(href)) {
            try {
              const u = new URL(href);
              const sharedUrl = u.searchParams.get(pattern.param);
              if (sharedUrl && sharedUrl.includes(window.location.host)) {
                const cleaned = cleanShareUrl(sharedUrl);
                if (cleaned !== sharedUrl) {
                  u.searchParams.set(pattern.param, cleaned);
                  a.href = u.toString();
                }
              }
            } catch (_) {
              // ignore
            }
            break;
          }
        }

        // Handle mailto: links
        if (href.startsWith("mailto:")) {
          try {
            const mailtoMatch = href.match(/^mailto:([^?]*)\??(.*)/);
            if (mailtoMatch) {
              const recipient = mailtoMatch[1] || "";
              const queryString = mailtoMatch[2] || "";
              const params = new URLSearchParams(queryString);
              let changed = false;

              for (const [key, val] of params.entries()) {
                if (val.includes(window.location.host)) {
                  const cleaned = cleanShareUrl(val);
                  if (cleaned !== val) {
                    params.set(key, cleaned);
                    changed = true;
                  }
                }
              }

              if (changed) {
                a.href = `mailto:${recipient}?${params.toString()}`;
              }
            }
          } catch (_) {
            // ignore
          }
        }
      });
    };

    shareDialogObserver = new MutationObserver(() => {
      setupModalButtonListeners();
      rewriteShareUrls();
    });

    shareDialogObserver.observe(doc.body, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
    });
  } catch (_e) {
    // Likely cross-origin; ignore.
  }
}

function setupShowcaseHighlightImageOverrides(
  iframe: HTMLIFrameElement,
  assetsBase: string,
) {
  try {
    const doc = iframe.contentDocument;
    if (!doc || !doc.body) return;

    // Disconnect previous observer (iframe can reload / navigate)
    if (showcaseHighlightsObserver) {
      showcaseHighlightsObserver.disconnect();
      showcaseHighlightsObserver = null;
    }

    const targetImageUrl = `${assetsBase}/images/highlight_img.png`;
    const targetLabel = "WEB入庫／試乗予約";

    const canonicalize = (input: unknown): string => {
      // Normalize slashes + whitespace so both '/' and '／' match.
      return String(input ?? "")
        .replace(/\s+/g, "")
        .replace(/[\/／]/g, "／")
        .trim();
    };

    const applyOverrides = () => {
      const boxes = doc.querySelectorAll(".highlight-box");
      boxes.forEach((box) => {
        const staticText = box.querySelector(
          ".static-text",
        ) as HTMLElement | null;
        if (!staticText) return;

        const label =
          canonicalize(staticText.textContent) ||
          canonicalize(staticText.getAttribute("aria-label"));
        if (label !== canonicalize(targetLabel)) return;

        const img = box.querySelector(".highlight-image") as HTMLElement | null;
        if (!img) return;

        img.style.backgroundImage = `url("${targetImageUrl}")`;
      });
    };

    // Apply once after load, then keep watching (highlights are rendered dynamically).
    applyOverrides();

    showcaseHighlightsObserver = new MutationObserver(() => {
      applyOverrides();
    });

    showcaseHighlightsObserver.observe(doc.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  } catch (_e) {
    // Likely cross-origin or blocked access; ignore.
  }
}

// Global state (like _test)
let attachmentsMap = new Map<string, any>();
let tagsMap = new Map<string, any>();
const loggedTagIds = new Set<string>();

function logTagInfo(tagId: string, tagData: any, reason: string) {
  try {
    const label = normalizeTagLabel(tagData?.label);
    const payload = {
      reason,
      id: tagId,
      label,
      description: tagData?.description,
      discPosition: tagData?.discPosition,
      anchorPosition: tagData?.anchorPosition,
      enabled: tagData?.enabled,
      color: tagData?.color,
      floorIndex: tagData?.floorIndex,
      type: tagData?.type,
    };
    console.log("[tag]", payload);
  } catch (e) {
    console.log("[tag]", reason, tagId);
  }
}

// Fullscreen. We fullscreen the whole page (document root), NOT the showcase
// iframe: a fullscreen iframe captures all pointer hit-testing, so body-level
// overlays (tooltips, billboard) would paint but not be clickable. Matterport's
// native fullscreen button (next to the VR button) is kept for its design and
// position; we just intercept its click to drive document-root fullscreen.
function toggleDocumentFullscreen() {
  const root = document.documentElement as any;
  const doc = document as any;
  const isFs = !!(document.fullscreenElement || doc.webkitFullscreenElement);
  try {
    if (isFs) {
      const ex = document.exitFullscreen || doc.webkitExitFullscreen;
      const r = ex && ex.call(document);
      if (r && typeof r.catch === "function") r.catch(() => {});
    } else {
      const req = root.requestFullscreen || root.webkitRequestFullscreen;
      const r = req && req.call(root);
      if (r && typeof r.catch === "function") r.catch(() => {});
    }
  } catch {
    /* noop */
  }
}

// Intercept Matterport's built-in fullscreen control (same-origin iframe) and
// redirect it to a document-root fullscreen. The user click inside the iframe is
// a genuine gesture and (same-origin) grants activation to this ancestor doc.
function setupFullscreenButtonOverride(iframe: HTMLIFrameElement) {
  let doc: Document | null = null;
  let win: any = null;
  try {
    doc = iframe.contentDocument;
    win = iframe.contentWindow;
  } catch {
    return;
  }
  if (!doc || !win) return;

  const findFsControl = (target: any): Element | null => {
    let el: any = target;
    for (let i = 0; el && i < 4; i++, el = el.parentElement) {
      if (el.classList && el.classList.contains("icon-fullscreen")) return el;
      if (
        el.querySelector &&
        el.querySelector(":scope > .icon-fullscreen, :scope > * > .icon-fullscreen")
      ) {
        return el;
      }
    }
    return null;
  };

  const block = (e: Event) => {
    if (!findFsControl(e.target)) return;
    // Stop Matterport from fullscreening the iframe itself.
    e.preventDefault();
    (e as any).stopImmediatePropagation();
    e.stopPropagation();
    if (e.type === "click") toggleDocumentFullscreen();
  };

  for (const type of [
    "pointerdown",
    "mousedown",
    "pointerup",
    "mouseup",
    "click",
  ]) {
    doc.addEventListener(type, block, true);
  }

  // Safety net: if the iframe ever does become the fullscreen element (e.g. an
  // interaction path we did not intercept), drop out of that broken state.
  const onFsChange = () => {
    if (document.fullscreenElement === iframe) {
      try {
        const r = document.exitFullscreen && document.exitFullscreen();
        if (r && typeof r.catch === "function") r.catch(() => {});
      } catch {
        /* noop */
      }
    }
  };
  document.addEventListener("fullscreenchange", onFsChange);
}

// Tooltip lifecycle (avoid duplicate subscriptions / RAF loops)
let tooltipPoseSubscription: { cancel: () => void } | null = null;
let tooltipOpenTagsSubscription: { cancel: () => void } | null = null;
let tooltipRafHandle: number | null = null;

function showCustomBillboard(
  customBillboard: HTMLElement,
  billboardTitle: HTMLElement,
  billboardContent: HTMLElement,
  tagData: any,
) {
  billboardTitle.textContent = tagData.label || "詳細情報";
  billboardContent.innerHTML = "";

  // Default billboard sizing
  setClass(customBillboard, "pano-billboard--fit-image", false);
  setClass(customBillboard, "pano-billboard--iframe", false);
  setClass(customBillboard, "pano-billboard--iframe-audi-info", false);
  setClass(customBillboard, "pano-billboard--iframe-audi-interior", false);

  const tagAttachments = (tagData.attachments || [])
    .map((attachmentId: string) => attachmentsMap.get(attachmentId))
    .filter(Boolean);

  // If the billboard is just a single image, shrink the container to the image width.
  if (
    tagAttachments.length === 1 &&
    tagAttachments[0]?.type === "tag.attachment.image"
  ) {
    setClass(customBillboard, "pano-billboard--fit-image", true);
  }

  if (tagAttachments.length > 0) {
    for (const attachment of tagAttachments) {
      if (attachment.type === "tag.attachment.image") {
        const img = document.createElement("img");
        img.src = attachment.src;
        img.alt = tagData.label || "";
        billboardContent.appendChild(img);
      } else if (attachment.type === "tag.attachment.video") {
        const video = document.createElement("video");
        video.src = attachment.src;
        video.controls = true;
        (video.style as any).maxWidth = "100%";
        (video.style as any).borderRadius = "4px";
        billboardContent.appendChild(video);
      }
    }
  } else if (tagData.description) {
    const iframeUrl = extractFirstHttpUrlFromText(tagData.description);
    if (iframeUrl && shouldShowUrlInIframe(iframeUrl)) {
      const iframe = document.createElement("iframe");
      iframe.src = iframeUrl;
      billboardContent.appendChild(iframe);
      setClass(customBillboard, "pano-billboard--iframe", true);

      // URL based sizing
      if (iframeUrl.includes("audi-interior")) {
        setClass(customBillboard, "pano-billboard--iframe-audi-interior", true);
      } else if (iframeUrl.includes("audi/information")) {
        setClass(customBillboard, "pano-billboard--iframe-audi-info", true);
      }
    } else {
      const p = document.createElement("p");
      p.textContent = tagData.description;
      p.style.lineHeight = "1.6";
      billboardContent.appendChild(p);
    }
  }

  setClass(customBillboard, "pano-billboard--visible", true);
}

function hideCustomBillboard(customBillboard: HTMLElement) {
  setClass(customBillboard, "pano-billboard--visible", false);
  setClass(customBillboard, "pano-billboard--fit-image", false);
  setClass(customBillboard, "pano-billboard--iframe", false);
  setClass(customBillboard, "pano-billboard--iframe-audi-info", false);
  setClass(customBillboard, "pano-billboard--iframe-audi-interior", false);
}

function createTagLabelTooltips(
  sdk: any,
  iframe: HTMLIFrameElement,
  alwaysVisibleLabels: string[],
  distanceThresholdMeters: number,
  onTooltipClick?: (tagId: string, tagData: any) => void,
) {
  if (tooltipPoseSubscription) {
    tooltipPoseSubscription.cancel();
    tooltipPoseSubscription = null;
  }
  if (tooltipOpenTagsSubscription) {
    tooltipOpenTagsSubscription.cancel();
    tooltipOpenTagsSubscription = null;
  }
  if (typeof tooltipRafHandle === "number") {
    cancelAnimationFrame(tooltipRafHandle);
    tooltipRafHandle = null;
  }

  const tooltipsMap = new Map<string, HTMLElement>();

  // Tooltips live at document.body level and are painted over the showcase.
  // Fullscreen is handled by fullscreening the whole page (document root) rather
  // than the iframe, so body-level overlays stay in the rendered/hit-tested
  // subtree — see setupOwnFullscreen() and the showcase iframe having no
  // allowfullscreen. (A fullscreen *iframe* would steal all hit-testing from
  // these overlays, so we deliberately avoid that.)
  const showTooltip = (el: HTMLElement) => {
    el.style.display = "block";
  };
  const hideTooltip = (el: HTMLElement) => {
    el.style.display = "none";
  };

  // Remove existing tooltips if any
  document.querySelectorAll(".pano-tag-tooltip").forEach((n) => n.remove());

  const alwaysVisibleSet = new Set(
    (alwaysVisibleLabels || [])
      .map((l) => normalizeTagLabel(l))
      .filter(Boolean),
  );

  tagsMap.forEach((tagData, tagId) => {
    const tooltip = document.createElement("div");
    tooltip.className = "pano-tag-tooltip";
    const displayLabel = normalizeTagLabel(tagData.label);
    tooltip.textContent = displayLabel;

    if (typeof onTooltipClick === "function") {
      tooltip.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        onTooltipClick(tagId, tagData);
      });
    }

    if (alwaysVisibleSet.has(displayLabel)) {
      tooltip.classList.add("pano-tag-tooltip--pinned");
    }

    document.body.appendChild(tooltip);
    tooltipsMap.set(tagId, tooltip);
  });

  let currentPose: any = null;
  tooltipPoseSubscription = sdk.Camera.pose.subscribe((pose: any) => {
    currentPose = pose;
  });

  // Tag hover distance logging (disabled)
  // let lastHoveredTagId: string | null = null;

  function updateTooltipPositions() {
    if (!currentPose) {
      requestAnimationFrame(updateTooltipPositions);
      return;
    }

    const iframeRect = iframe.getBoundingClientRect();
    const showcaseSize = { w: iframeRect.width, h: iframeRect.height };

    tooltipsMap.forEach((tooltip, tagId) => {
      const tagData = tagsMap.get(tagId);
      if (!tagData || !tagData.discPosition) return;

      const dx = tagData.discPosition.x - currentPose.position.x;
      const dy = tagData.discPosition.y - currentPose.position.y;
      const dz = tagData.discPosition.z - currentPose.position.z;
      const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);

      const isAlwaysVisible = alwaysVisibleLabels
        .map((l) => normalizeTagLabel(l))
        .includes(normalizeTagLabel(tagData.label));
      const shouldShowAlwaysVisible =
        isAlwaysVisible && distance <= distanceThresholdMeters;

      const screenPos = sdk.Conversion.worldToScreen(
        tagData.discPosition,
        currentPose,
        showcaseSize,
      );

      if (
        screenPos.x >= 0 &&
        screenPos.x <= showcaseSize.w &&
        screenPos.y >= 0 &&
        screenPos.y <= showcaseSize.h &&
        screenPos.z > 0
      ) {
        const dx = currentPose.position.x - tagData.discPosition.x;
        const dy = currentPose.position.y - tagData.discPosition.y;
        const dz = currentPose.position.z - tagData.discPosition.z;
        const distanceM = Math.sqrt(dx * dx + dy * dy + dz * dz);

        const yOffset = calcYOffsetPx(distanceM);
        const pageX = iframeRect.left + screenPos.x;
        const pageY = iframeRect.top + screenPos.y - yOffset;

        tooltip.style.left = `${pageX}px`;
        tooltip.style.top = `${pageY}px`;
        tooltip.style.transform = "translateX(-50%)";

        if (
          shouldShowAlwaysVisible ||
          tooltip.classList.contains("pano-tag-tooltip--visible")
        ) {
          showTooltip(tooltip);
        } else {
          hideTooltip(tooltip);
        }
      } else {
        hideTooltip(tooltip);
      }
    });

    tooltipRafHandle = requestAnimationFrame(updateTooltipPositions);
  }

  function clamp(n: number, min: number, max: number) {
    return Math.max(min, Math.min(max, n));
  }
  function lerp(a: number, b: number, t: number) {
    return a + (b - a) * t;
  }

  function calcYOffsetPx(distanceM: number) {
    const baseD = 1.2;
    let baseOffset = 65;
    //const w = window.innerWidth;
    const h = window.innerHeight;
    if (h >= 1000) {
      baseOffset += 8;
    }

    // 近距離側（被りやすい）：上に逃がす量を増やす
    const nearD = 0.5; // ここまで近づくと最大寄せ
    const nearOffset = 70; // 0.6mのときのオフセット

    // 遠距離側（離れやすい）：上に逃がす量を減らす
    const farD = 8.0; // ここ以上は遠い側の上限扱い
    const farOffset = 55; // 8mのときのオフセット

    if (distanceM <= baseD) {
      // distanceM: baseD(1.2)->nearD(0.6) の範囲で 65->90 に補間
      const t = clamp((baseD - distanceM) / (baseD - nearD), 0, 1);
      return lerp(baseOffset, nearOffset, t);
    } else {
      // distanceM: baseD(1.2)->farD(8.0) の範囲で 65->45 に補間
      const t = clamp((distanceM - baseD) / (farD - baseD), 0, 1);
      return lerp(baseOffset, farOffset, t);
    }
  }

  tooltipOpenTagsSubscription = sdk.Tag.openTags.subscribe({
    onChanged: (openTags: any) => {
      const pickFirstTagId = (value: any): string | null => {
        if (!value) return null;
        if (typeof value === "string") return value;
        if (typeof value === "object") {
          const maybeId = (value as any).id ?? (value as any).sid;
          if (typeof maybeId === "string") return maybeId;
        }
        if (Array.isArray(value)) {
          return pickFirstTagId(value[0]);
        }
        const iterable = (value as any)?.[Symbol.iterator];
        if (typeof iterable === "function") {
          for (const v of value as any) {
            const picked = pickFirstTagId(v);
            if (picked) return picked;
          }
        }
        return null;
      };

      const hoveredTagId =
        pickFirstTagId(openTags?.hovered) ||
        pickFirstTagId(openTags?.hoveredTagId) ||
        pickFirstTagId(openTags?.hoveredTag) ||
        pickFirstTagId(openTags?.hoveredTags) ||
        pickFirstTagId(openTags?.highlighted) ||
        null;

      // NOTE: Distance logging was requested earlier but is currently disabled.
      // If you need it again, restore the block that calculates the distance from
      // `currentPose.position` to `tagData.discPosition/anchorPosition` and logs it.
      /*let lastHoveredTagId: string | null = null;

      if (hoveredTagId && hoveredTagId !== lastHoveredTagId) {
        lastHoveredTagId = hoveredTagId;
        const tagData = tagsMap.get(hoveredTagId);
        const tagPos = tagData?.discPosition || tagData?.anchorPosition;
        const camPos = currentPose?.position;
        if (tagPos && camPos) {
          const dx = tagPos.x - camPos.x;
          const dy = tagPos.y - camPos.y;
          const dz = tagPos.z - camPos.z;
          const distanceMeters = Math.sqrt(dx * dx + dy * dy + dz * dz);
          const label = String(tagData?.label || hoveredTagId);
          console.log(`[tag hover] ${label}: ${distanceMeters.toFixed(2)}m`);
        } else {
          console.log(`[tag hover] ${hoveredTagId}: distance unavailable`);
        }
      } else if (!hoveredTagId) {
        lastHoveredTagId = null;
      }*/

      tooltipsMap.forEach((tooltip) => {
        tooltip.classList.remove("pano-tag-tooltip--visible");
      });

      if (hoveredTagId) {
        const tooltip = tooltipsMap.get(hoveredTagId);
        if (tooltip) tooltip.classList.add("pano-tag-tooltip--visible");
      }
    },
  });

  updateTooltipPositions();
}

async function main() {
  const showcase = document.getElementById(
    "showcase",
  ) as HTMLIFrameElement | null;
  const menuButton = document.getElementById(
    "menu-button",
  ) as HTMLButtonElement | null;
  const closeMenuButton = document.getElementById(
    "close-menu",
  ) as HTMLButtonElement | null;
  const sideMenu = document.getElementById("side-menu") as HTMLElement | null;
  const customBillboard = document.getElementById(
    "custom-billboard",
  ) as HTMLElement | null;
  const billboardTitle = document.getElementById(
    "billboard-title",
  ) as HTMLElement | null;
  const billboardContent = document.getElementById(
    "billboard-content",
  ) as HTMLElement | null;
  const billboardClose = document.getElementById(
    "billboard-close",
  ) as HTMLButtonElement | null;
  const buttonContainer = document.querySelector(
    ".pano-actions",
  ) as HTMLElement | null;

  const shopSlug = getShopSlug();
  let ga4Context: { shop_slug: string; model_id?: string } = {
    shop_slug: shopSlug,
  };
  const track = (eventName: string, params: Ga4Params = {}) => {
    ga4Event(eventName, { ...ga4Context, ...params });
  };

  // Keep SDK + selected tag state so we can fully close (including SDK selection) from UI events
  let sdkRef: any = null;
  let currentOpenTagId: string | null = null;

  const closeBillboard = async () => {
    const wasVisible = customBillboard.classList.contains(
      "pano-billboard--visible",
    );
    const closingTagId = currentOpenTagId;
    const closingTagLabel = closingTagId
      ? normalizeTagLabel(tagsMap.get(closingTagId)?.label)
      : "";

    hideCustomBillboard(customBillboard);

    if (wasVisible && closingTagId) {
      track("audi_tag_close", {
        tag_id: closingTagId,
        tag_label: closingTagLabel,
      });
    }

    if (sdkRef && currentOpenTagId) {
      const id = currentOpenTagId;
      currentOpenTagId = null; // prevent re-entrancy
      try {
        await sdkRef.Tag.close(id);
      } catch (e) {
        console.warn("sdk.Tag.close failed:", e);
      }
    } else {
      currentOpenTagId = null;
    }
  };

  let menuSpaceInfoElm: HTMLElement | null = null;
  let menuSummaryElm: HTMLElement | null = null;

  if (!showcase || !menuButton || !closeMenuButton || !sideMenu) {
    throw new Error("Missing required layout elements in HTML template");
  }
  if (
    !customBillboard ||
    !billboardTitle ||
    !billboardContent ||
    !billboardClose
  ) {
    throw new Error("Missing billboard elements in HTML template");
  }
  if (!buttonContainer) {
    throw new Error("Missing .pano-actions in HTML template");
  }

  const defaultAssetsBase = "/audi-assets";
  const config = await fetchJson<ShopConfig>(`${defaultAssetsBase}/shop.json`);

  const assetsBase = config.ui?.assetsBasePath || defaultAssetsBase;

  const sdkVersion = config.matterportSdk?.sdkVersion || "3.6";
  const configKey = (config.matterportSdk?.key || "").trim();
  const mpSdkKey =
    (__MP_SDK_KEY__ || "").trim() ||
    (configKey && configKey !== "PUT_YOUR_MATTERPORT_SDK_KEY_HERE"
      ? configKey
      : "");
  const shop = config.shops.find((s) => deriveSlugFromShop(s) === shopSlug);
  if (!shop) {
    document.body.textContent = `Unknown shop: ${shopSlug}`;
    return;
  }

  let derivedServiceBookingUrl = "";
  let derivedTestDriveUrl = "";

  const renderActionButtons = () => {
    // Buttons (top-right)
    buttonContainer.innerHTML = "";

    const ctaLinks: Array<{ label: string; href: string }> = [
      ...(shop.ctaLinks || []),
    ];

    if (derivedTestDriveUrl) {
      ctaLinks.push({ label: "試乗予約", href: derivedTestDriveUrl });
    }
    if (derivedServiceBookingUrl) {
      ctaLinks.push({ label: "WEB入庫", href: derivedServiceBookingUrl });
    }

    for (const link of ctaLinks) {
      const a = document.createElement("a");
      a.className = "pano-actions__button";
      a.textContent = link.label;
      a.href = link.href;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      a.addEventListener("click", () => {
        const href = String(link.href || "").trim();
        const label = String(link.label || "").trim();
        const ctaType =
          href && href === derivedServiceBookingUrl
            ? "service_booking"
            : href && href === derivedTestDriveUrl
              ? "test_drive"
              : "cta";

        track("audi_cta_click", {
          cta_type: ctaType,
          cta_label: label,
          destination_host: safeDestinationHost(href),
        });
      });
      buttonContainer.appendChild(a);
    }

    if (ctaLinks.length === 0) hide(buttonContainer);
    else show(buttonContainer);
  };

  function openInNewTab(url: string) {
    const u = String(url || "").trim();
    if (!u) return;
    const w = window.open(u, "_blank", "noopener,noreferrer");
    if (w) w.opener = null;
  }

  function formatAddressForMenu(address: string): string {
    const a = String(address || "").trim();
    if (!a) return "";
    return a.replace(/,\s*JP\s*$/i, "");
  }

  function formatPhoneForMenu(phone: string): string {
    const raw = String(phone || "").trim();
    if (!raw) return "";
    if (!raw.startsWith("+") && raw.includes("-")) return raw;

    let normalized = raw;
    if (normalized.startsWith("+")) {
      normalized = "+" + normalized.slice(1).replace(/\D/g, "");
    } else {
      normalized = normalized.replace(/\D/g, "");
    }

    if (normalized.startsWith("+81")) {
      normalized = "0" + normalized.slice(3);
    } else if (normalized.startsWith("81") && !normalized.startsWith("0")) {
      normalized = "0" + normalized.slice(2);
    }

    const digits = normalized.replace(/\D/g, "");
    if (
      digits.length === 10 &&
      (digits.startsWith("03") || digits.startsWith("06"))
    ) {
      return `${digits.slice(0, 2)}-${digits.slice(2, 6)}-${digits.slice(6)}`;
    }
    if (digits.length === 10) {
      return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    if (digits.length === 11) {
      return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
    }
    return raw;
  }

  function tryOpenLinkForTag(tagData: any): string | null {
    const label = normalizeTagLabel(tagData?.label);
    const url = extractFirstHttpUrlFromText(tagData?.description);
    if (!url) return null;

    // If this URL is intended to be shown in the custom billboard iframe, do so.
    if (shouldShowUrlInIframe(url)) {
      return null;
    }

    track("audi_outbound_click", {
      link_type: "tag_auto_open",
      destination_host: safeDestinationHost(url),
      tag_id: String(tagData?.id || ""),
      tag_label: label,
    });
    openInNewTab(url);
    return url;
  }
  if (!mpSdkKey) {
    document.body.textContent =
      "Missing MP SDK key. Set MP_SDK_KEY in .env (recommended) or set matterportSdk.key in shop.json";
    return;
  }
  const modelId = shop.matterport?.modelId;
  if (!modelId) {
    document.body.textContent = "Missing matterport.modelId in shop.json";
    return;
  }

  ga4Context = { shop_slug: shopSlug, model_id: modelId };
  track("audi_shop_view", {
    page_path: window.location.pathname,
  });

  // Buttons (top-right) will be rendered from tag-derived URLs.
  hide(buttonContainer);

  // Side menu items (fill existing HTML)
  const menuShopImageWrap = sideMenu.querySelector(
    "#menu-shop-image-wrap",
  ) as HTMLElement | null;
  const menuShopImage = sideMenu.querySelector(
    "#menu-shop-image",
  ) as HTMLImageElement | null;
  const menuContact = sideMenu.querySelector(
    "#menu-contact",
  ) as HTMLElement | null;
  const menuSite = sideMenu.querySelector("#menu-site") as HTMLElement | null;

  const otherHeader = sideMenu.querySelector(
    "#menu-other-shops-header",
  ) as HTMLElement | null;
  const otherList = sideMenu.querySelector(
    "#menu-other-shops-list",
  ) as HTMLElement | null;
  const otherItems = sideMenu.querySelector(
    "#menu-other-shops-items",
  ) as HTMLElement | null;

  menuSpaceInfoElm = sideMenu.querySelector(
    "#menu-space-info",
  ) as HTMLElement | null;
  menuSummaryElm = sideMenu.querySelector(
    "#menu-summary",
  ) as HTMLElement | null;

  // 店舗画像
  if (menuShopImageWrap && menuShopImage && shop.imageUrl) {
    menuShopImage.alt = shop.name?.full || shop.id;
    menuShopImage.src = `${assetsBase}/images/${shop.imageUrl}`;
    show(menuShopImageWrap);
  } else if (menuShopImageWrap) {
    hide(menuShopImageWrap);
  }

  // お問い合わせ
  if (menuContact && shop.contactUrl) {
    show(menuContact);
    menuContact.addEventListener("click", () => {
      track("audi_outbound_click", {
        link_type: "menu_contact",
        destination_host: safeDestinationHost(shop.contactUrl!),
      });
      openInNewTab(shop.contactUrl!);
      closeMenu();
    });
  } else if (menuContact) {
    hide(menuContact);
  }

  // 店舗サイト
  if (menuSite && shop.siteUrl) {
    show(menuSite);
    menuSite.addEventListener("click", () => {
      track("audi_outbound_click", {
        link_type: "menu_site",
        destination_host: safeDestinationHost(shop.siteUrl!),
      });
      openInNewTab(shop.siteUrl!);
      closeMenu();
    });
  } else if (menuSite) {
    hide(menuSite);
  }

  // その他のバーチャルショールーム（アコーディオン）
  if (otherHeader && otherList && otherItems) {
    otherItems.innerHTML = "";
    const otherShops = (config.shops || []).filter((s) => s.id !== shop.id);
    for (const s of otherShops) {
      const url = s.embed?.url;
      if (!url) continue;

      const itemDiv = document.createElement("div");
      itemDiv.className = "pano-menu__item";

      const labelDiv = document.createElement("div");
      labelDiv.className = "pano-menu__shop-item";
      labelDiv.textContent = s.name?.full || s.id;
      itemDiv.appendChild(labelDiv);

      itemDiv.addEventListener("click", () => {
        track("audi_outbound_click", {
          link_type: "other_shop",
          destination_host: safeDestinationHostWithPath(url),
        });
        openInNewTab(url);
        closeMenu();
      });

      otherItems.appendChild(itemDiv);
    }

    if (otherItems.childElementCount > 0) {
      show(otherHeader);
      hide(otherList);
      otherHeader.setAttribute("aria-expanded", "false");
      otherList.setAttribute("aria-hidden", "true");

      let accordionOpen = false;
      otherHeader.addEventListener("click", () => {
        accordionOpen = !accordionOpen;
        otherHeader.setAttribute(
          "aria-expanded",
          accordionOpen ? "true" : "false",
        );
        otherList.style.display = accordionOpen ? "" : "none";
        otherList.setAttribute("aria-hidden", accordionOpen ? "false" : "true");
      });
    } else {
      hide(otherHeader);
      hide(otherList);
    }
  }

  // Enable menu UI
  show(menuButton);
  show(sideMenu);

  function openMenu() {
    sideMenu.classList.add("pano-menu--open");
    setClass(menuButton, "pano-menu-button--hidden", true);
  }
  function closeMenu() {
    sideMenu.classList.remove("pano-menu--open");
    setClass(menuButton, "pano-menu-button--hidden", false);
  }

  menuButton.addEventListener("click", openMenu);
  closeMenuButton.addEventListener("click", closeMenu);

  billboardClose.addEventListener("click", () => {
    void closeBillboard();
  });

  // Build Showcase URL
  const viewerLoadStartedAt =
    typeof performance !== "undefined" ? performance.now() : Date.now();
  const params = new URLSearchParams({
    m: modelId,
    play: "1",
    qs: "1",
    log: "0",
    applicationKey: mpSdkKey,
  });

  const extraParams = config.ui?.showcaseQueryParams || {
    brand: "0",
    title: "0",
    search: "0",
    ts: "1",
  };
  for (const [k, v] of Object.entries(extraParams)) params.set(k, v);

  // URL params should take priority over config defaults.
  applySweepParamsFromPageUrl(params);
  showcase.src = `${assetsBase}/bundle/showcase.html?${params.toString()}`;

  // Connect SDK once iframe loaded
  showcase.addEventListener("load", async () => {
    // Inject per-load, because the iframe can navigate/reload.
    injectCssIntoIframeHead(showcase, `${assetsBase}/showcase_custom.css`);
    setupShowcaseHighlightImageOverrides(showcase, assetsBase);
    setupShareLinkOverrides(showcase);
    setupFullscreenButtonOverride(showcase);

    let sdk: any;
    try {
      sdk = await showcase.contentWindow!.MP_SDK.connect(
        showcase,
        mpSdkKey,
        sdkVersion,
      );
      sdkRef = sdk;
    } catch (e) {
      console.error(e);
      return;
    }

    const viewerLoadedAt =
      typeof performance !== "undefined" ? performance.now() : Date.now();
    const loadMs = Math.max(
      0,
      Math.round(viewerLoadedAt - viewerLoadStartedAt),
    );
    track("audi_viewer_loaded", {
      load_ms: loadMs,
      sdk_version: sdkVersion,
    });

    // Set share link to use the parent page URL without extra params
    try {
      await sdk.Link.setShareLinkPolicy(sdk.Link.CreationPolicy.REFERRER, {
        includeParams: [],
      });
    } catch (e) {
      console.warn("Failed to set share link policy:", e);
    }

    const spaceDetails = await sdk.Model.getDetails();

    if (menuSpaceInfoElm) {
      const address = formatAddressForMenu(spaceDetails?.formattedAddress);
      const phone = formatPhoneForMenu(spaceDetails?.formattedContactPhone);
      const parts: string[] = [];
      if (address) parts.push(address);
      if (phone) parts.push(phone);
      menuSpaceInfoElm.textContent = parts.join("\n");
    }

    if (menuSummaryElm) {
      const summary = String(spaceDetails?.summary || "").trim();
      menuSummaryElm.textContent = summary;
    }

    // Attachments
    sdk.Tag.attachments.subscribe({
      onCollectionUpdated: (attachments: any) => {
        attachmentsMap = new Map(attachments);
      },
    });

    // Tags: disable default billboard + actions, then create tooltips
    sdk.Tag.data.subscribe({
      onCollectionUpdated: async (collection: any) => {
        const prevIds = new Set(tagsMap.keys());
        tagsMap = new Map(collection);

        // Derive URLs from tag descriptions
        let nextServiceBookingUrl = derivedServiceBookingUrl;
        let nextTestDriveUrl = derivedTestDriveUrl;
        for (const [_id, tagData] of tagsMap) {
          const label = normalizeTagLabel(tagData?.label);
          const url = extractFirstHttpUrlFromText(tagData?.description);
          if (!url) continue;

          if (
            !nextServiceBookingUrl &&
            (label === "WEB入庫" || label === "WEB入庫予約")
          ) {
            nextServiceBookingUrl = url;
          }

          if (
            !nextTestDriveUrl &&
            (label === "試乗予約" || label === "試乗車検索")
          ) {
            nextTestDriveUrl = url;
          }
        }

        const urlsChanged =
          nextServiceBookingUrl !== derivedServiceBookingUrl ||
          nextTestDriveUrl !== derivedTestDriveUrl;
        derivedServiceBookingUrl = nextServiceBookingUrl;
        derivedTestDriveUrl = nextTestDriveUrl;
        if (urlsChanged) renderActionButtons();

        // Log tag info (first time: all tags, then only newly observed tags)
        for (const [tagId, tagData] of tagsMap) {
          const id = String(tagId || "");
          if (!id) continue;
          if (loggedTagIds.has(id)) continue;
          if (prevIds.size > 0 && prevIds.has(id)) continue;
          loggedTagIds.add(id);
          logTagInfo(id, tagData, "collectionUpdated");
        }

        for (const [tagId] of collection) {
          try {
            await sdk.Tag.editBillboard(tagId, { visible: false });
            await sdk.Tag.allowAction(tagId, {
              opening: false,
              navigating: false,
              docking: false,
            });
          } catch (_) {
            // ignore per-tag failures
          }
        }

        const alwaysVisible = config.ui?.alwaysVisibleTagLabels || [];
        const distanceThreshold =
          typeof config.ui?.alwaysVisibleTagDistanceMeters === "number"
            ? config.ui!.alwaysVisibleTagDistanceMeters!
            : 5.0;

        createTagLabelTooltips(
          sdk,
          showcase,
          alwaysVisible,
          distanceThreshold,
          (_tagId, tagData) => {
            tryOpenLinkForTag(tagData);
          },
        );
      },
    });

    // Tag click => custom billboard
    sdk.Tag.openTags.subscribe({
      onChanged: (openTags: any) => {
        const selectedTagIds = Array.from(openTags.selected) as string[];
        const newTagId: string | null = selectedTagIds[0] || null;

        if (newTagId && newTagId !== currentOpenTagId) {
          currentOpenTagId = newTagId;
          const tagData = tagsMap.get(newTagId);
          if (tagData) {
            logTagInfo(String(newTagId), tagData, "selected");
            const autoOpenedUrl = tryOpenLinkForTag(tagData);
            if (autoOpenedUrl) {
              void closeBillboard();
              return;
            }

            const tagLabel = normalizeTagLabel(tagData?.label);
            const attachmentIds = Array.isArray(tagData?.attachments)
              ? tagData.attachments
              : [];
            track("audi_tag_open", {
              tag_id: String(newTagId),
              tag_label: tagLabel,
              tag_has_media: attachmentIds.length > 0,
            });

            const embeddedUrl = extractFirstHttpUrlFromText(
              tagData?.description,
            );
            if (embeddedUrl) {
              track("audi_outbound_click", {
                link_type: "tag_billboard_embedded",
                destination_host: safeDestinationHost(embeddedUrl),
                tag_id: String(newTagId),
                tag_label: tagLabel,
              });
            }

            showCustomBillboard(
              customBillboard,
              billboardTitle,
              billboardContent,
              tagData,
            );
          }
        } else if (!newTagId && currentOpenTagId) {
          void closeBillboard();
        }
      },
    });

    // Tag icons: default + per-label overrides (optional)
    const overrides = normalizeTagIconOverrides(config, assetsBase);
    const defaultTagIcon = String(config.ui?.defaultTagIcon || "").trim();
    const defaultIconUrl = defaultTagIcon
      ? resolveIconUrl(defaultTagIcon, assetsBase)
      : "";
    const defaultIconId = defaultIconUrl
      ? `audi-ui-default-icon-${stableHash(defaultIconUrl)}`
      : "";

    if (defaultIconId || overrides.length > 0) {
      try {
        if (defaultIconId) {
          await sdk.Asset.registerTexture(defaultIconId, defaultIconUrl);
        }
        for (const o of overrides) {
          await sdk.Asset.registerTexture(o.iconId, o.iconUrl);
        }

        const appliedIconByTagId = new Map<string, string>();
        const resolveIconIdForTag = (tag: any): string => {
          const label = normalizeTagLabel(tag?.label);
          const override = overrides.find((o) => o.matchLabel === label);
          return override?.iconId || defaultIconId;
        };
        const applyIconToTag = async (tag: any) => {
          const targetIconId = resolveIconIdForTag(tag);
          if (!targetIconId) return;
          const tagId = String(tag?.id || "");
          if (!tagId) return;

          const last = appliedIconByTagId.get(tagId);
          if (last === targetIconId) return;

          appliedIconByTagId.set(tagId, targetIconId);
          await sdk.Tag.editIcon(tagId, targetIconId);
        };

        // Apply on current collection and on new tags
        sdk.Tag.data.subscribe({
          onAdded: async (_id: string, tag: any) => {
            try {
              await applyIconToTag(tag);
            } catch (_) {
              // ignore per-tag failures
            }
          },
          onCollectionUpdated: async (collection: any) => {
            for (const [_id, tag] of collection) {
              try {
                await applyIconToTag(tag);
              } catch (_) {
                // ignore per-tag failures
              }
            }
          },
        });
      } catch (e) {
        console.warn("tag icons setup failed:", e);
      }
    }
  });
}

main().catch((e) => console.error(e));
